/**
 * RegistryAdoption — one-time migration operator screen (plan §7.5, P3).
 *
 * Super-admin only. For every study still holding protocol-scoped legacy
 * Intelligence documents: ADOPT copies them into eReg (bytes + provenance,
 * auto-pin into empty protocol/ICF/IB slots, text re-extracted through the
 * shared ladder), a human confirms slot pins in eReg, then RETIRE hides the
 * legacy rows once eReg extraction is complete — chat serves the registry from
 * that point on. Assisted, not magic.
 */
import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  DatabaseZap, FolderOpen, CheckCircle, XCircle, Loader2, ArrowRight, Pin, RefreshCw
} from 'lucide-react';
import api from '../../services/api';
import { PageHeader } from '../../ui';
import { eregStudyLink } from '../../ui/returnTo';

interface AdoptionDoc {
  id: string;
  filename: string;
  title: string | null;
  document_type: string | null;
  mime_type: string | null;
  adopted: boolean;
  registry_document_id: string | null;
  registry_extraction_status: string | null;
}

interface AdoptionProtocol {
  protocol_id: string;
  protocol_number: string | null;
  study_nickname: string | null;
  sponsor_name: string | null;
  site_id: string | null;
  site_name: string | null;
  slots: { protocol: boolean; icf: boolean };
  documents: AdoptionDoc[];
}

const RETURN_TO = '/intelligence/registry-adoption';

export function RegistryAdoption() {
  const [protocols, setProtocols] = useState<AdoptionProtocol[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null); // protocolId:action
  const [messages, setMessages] = useState<Record<string, { kind: 'ok' | 'error'; text: string }>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/intelligence/registry-adoption');
      setProtocols(res.data?.protocols || []);
    } catch (error) {
      console.error('Error loading adoption status:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setMessage = (protocolId: string, kind: 'ok' | 'error', text: string) =>
    setMessages(prev => ({ ...prev, [protocolId]: { kind, text } }));

  const adopt = async (protocolId: string) => {
    setBusy(`${protocolId}:adopt`);
    try {
      const res = await api.post(`/api/intelligence/registry-adoption/${protocolId}/adopt`);
      const { adopted, skipped, failed, documents } = res.data;
      const needsPin = (documents || []).filter((d: any) => d.needs_pin).length;
      setMessage(
        protocolId,
        failed > 0 ? 'error' : 'ok',
        `Adopted ${adopted}, skipped ${skipped}, failed ${failed}.` +
          (needsPin > 0 ? ` ${needsPin} document(s) need a slot decision in eReg.` : '')
      );
      await load();
    } catch (error: any) {
      setMessage(protocolId, 'error', error.response?.data?.error || 'Adoption failed');
    } finally {
      setBusy(null);
    }
  };

  const retire = async (protocolId: string) => {
    setBusy(`${protocolId}:retire`);
    try {
      const res = await api.post(`/api/intelligence/registry-adoption/${protocolId}/retire`);
      setMessage(protocolId, 'ok', `Retired ${res.data.retired} legacy document(s).`);
      await load();
    } catch (error: any) {
      const data = error.response?.data;
      const reason =
        data?.error === 'extraction_pending'
          ? 'eReg text extraction is still running for adopted documents — try again in a few minutes.'
          : data?.error === 'no_pinned_chat_docs'
            ? 'Pin the adopted protocol/ICF into eReg key-document slots first (chat would go dark).'
            : data?.error === 'nothing_adopted'
              ? 'Nothing adopted yet — run Adopt first.'
              : data?.error || 'Retire failed';
      setMessage(protocolId, 'error', reason);
    } finally {
      setBusy(null);
    }
  };

  const extractionPill = (status: string | null) => {
    if (status === 'complete') {
      return <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/40 dark:text-green-300"><CheckCircle size={12} /> text ready</span>;
    }
    if (status === 'failed') {
      return <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700 dark:bg-red-900/40 dark:text-red-300"><XCircle size={12} /> extraction failed</span>;
    }
    if (status === 'unsupported') {
      return <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-300">no text (unsupported)</span>;
    }
    if (status) {
      return <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/40 dark:text-blue-300"><Loader2 size={12} className="animate-spin" /> extracting</span>;
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 dark:border-primary-400"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <PageHeader
        className="mb-2"
        title="Registry adoption"
        icon={DatabaseZap}
        subtitle="One-time migration: move protocol-scoped Intelligence documents into eReg, the platform document source of truth"
        actions={
          <button
            onClick={load}
            className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
          >
            <RefreshCw size={16} /> Refresh
          </button>
        }
      />
      <p className="mb-6 text-sm text-gray-500 dark:text-gray-400">
        Per study: <span className="font-medium">1. Adopt</span> copies the files into eReg (empty
        protocol/ICF slots pin automatically) · <span className="font-medium">2. Pin</span> — confirm
        key-document slots in eReg · <span className="font-medium">3. Retire</span> hides the legacy
        copies once eReg text extraction completes. Chat falls back to legacy files until then, so
        nothing goes dark mid-migration.
      </p>

      {protocols.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 p-10 text-center dark:border-gray-600">
          <CheckCircle size={48} className="mx-auto mb-4 text-green-500" />
          <h3 className="text-lg font-medium text-gray-900 mb-1 dark:text-gray-100">Nothing left to adopt</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No protocol-scoped legacy Intelligence documents remain — every study reads from eReg.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {protocols.map(p => {
            const unadopted = p.documents.filter(d => !d.adopted);
            const adoptedDocs = p.documents.filter(d => d.adopted);
            const msg = messages[p.protocol_id];
            return (
              <div key={p.protocol_id} className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <FolderOpen size={18} className="flex-shrink-0 text-primary-600 dark:text-primary-400" />
                    <div className="min-w-0">
                      <p className="truncate font-semibold text-gray-900 dark:text-gray-100">
                        {p.protocol_number || 'Unknown study'}
                        {p.study_nickname && <span className="ml-2 font-normal text-gray-500 dark:text-gray-400">{p.study_nickname}</span>}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {p.site_name || 'Unknown site'}
                        {' · '}slots: protocol {p.slots.protocol ? '✓' : '—'}, ICF {p.slots.icf ? '✓' : '—'}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Link
                      to={eregStudyLink(p.protocol_id, { returnTo: RETURN_TO, returnLabel: 'Registry adoption' })}
                      className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
                    >
                      <Pin size={14} /> Pin in eReg
                    </Link>
                    <button
                      onClick={() => adopt(p.protocol_id)}
                      disabled={busy !== null || unadopted.length === 0}
                      className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0"
                    >
                      {busy === `${p.protocol_id}:adopt` ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
                      Adopt into eReg ({unadopted.length})
                    </button>
                    <button
                      onClick={() => retire(p.protocol_id)}
                      disabled={busy !== null || adoptedDocs.length === 0}
                      className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
                    >
                      {busy === `${p.protocol_id}:retire` ? <Loader2 size={16} className="animate-spin" /> : null}
                      Retire legacy ({adoptedDocs.length})
                    </button>
                  </div>
                </div>

                {msg && (
                  <p className={`mt-2 text-sm ${msg.kind === 'ok' ? 'text-green-700 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                    {msg.text}
                  </p>
                )}

                <ul className="mt-3 divide-y divide-gray-100 dark:divide-gray-700">
                  {p.documents.map(d => (
                    <li key={d.id} className="flex flex-wrap items-center gap-3 py-2 sm:flex-nowrap">
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm text-gray-900 dark:text-gray-100">{d.title || d.filename}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{d.document_type || 'document'} · {d.mime_type || 'file'}</p>
                      </div>
                      {d.adopted ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-700 dark:bg-primary-900/40 dark:text-primary-300">
                          in eReg
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/20 dark:text-amber-400">
                          not adopted
                        </span>
                      )}
                      {d.adopted && extractionPill(d.registry_extraction_status)}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default RegistryAdoption;
